import { supabase } from '../supabaseClient';
import { logAudit } from './auditLog';
import { createNotification, sendEmailNotification, buildEmailHtml, sendTeamsNotification } from './notifications';
import { isNotificationEnabled } from './notificationPreferences';
import { updateTicketActivity } from './ticketActivity';
import { getCachedUserAndProfile } from '../utils/userCache';

/**
 * 取得 Ticket 的所有指派人
 */
export const getAssigneesByTicket = async (ticketId) => {
  try {
    const { data, error } = await supabase
      .from('ticket_assignees')
      .select(`
        ticket_id,
        user_id,
        assigned_at,
        assigned_by,
        profiles:user_id (id, email)
      `)
      .eq('ticket_id', ticketId)
      .order('assigned_at', { ascending: true });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching assignees:', error);
    return { data: null, error };
  }
};

/**
 * 確保用戶在 profiles 存在
 * 遷移後同 DB，profiles 就是主系統的 profiles 表
 * 回傳 profiles.id（= 主系統用戶 ID）
 */
const ensureProfile = async (mainSystemUser) => {
  if (!mainSystemUser?.id) return null;

  // 遷移後同 DB，直接用主系統 ID 查詢
  const { data: existing } = await supabase
    .from('profiles')
    .select('id')
    .eq('id', mainSystemUser.id)
    .maybeSingle();

  if (existing) return existing.id;

  // 用 email 嘗試查詢（相容舊資料）
  if (mainSystemUser.email) {
    const { data: byEmail } = await supabase
      .from('profiles')
      .select('id')
      .ilike('email', mainSystemUser.email)
      .maybeSingle();

    if (byEmail) return byEmail.id;
  }

  // 找不到用戶（主系統沒有此帳號），回傳原始 ID
  console.warn('⚠️ ensureProfile: user not found in profiles:', mainSystemUser.email);
  return mainSystemUser.id;
};

/**
 * 新增指派人
 * @param {object} targetUser - 被指派者的完整資訊（來自主系統 API）
 */
export const addAssignee = async (ticketId, userId, assignedBy, targetUser = null, { skipAudit = false } = {}) => {
  try {
    // 確保被指派者的 profile 存在，取得子系統 profile ID
    const subsystemUserId = targetUser ? await ensureProfile(targetUser) : userId;

    const { data, error } = await supabase
      .from('ticket_assignees')
      .insert({
        ticket_id: ticketId,
        user_id: subsystemUserId,
        assigned_by: assignedBy,
      })
      .select(`
        ticket_id,
        user_id,
        assigned_at,
        profiles:user_id (id, email)
      `)
      .single();

    if (error) throw error;

    // 查 ticket 的 company_id / department_id / title（用於 audit log + 通知）
    const { data: ticketInfo } = await supabase
      .from('tickets')
      .select('company_id, department_id, title, ticket_no, projects:project_id (name)')
      .eq('id', ticketId)
      .single();

    // 記錄指派異動（初次建立時由 createTicket 統一記錄，跳過）
    if (!skipAudit) {
      const resolvedName = targetUser?.name || data.profiles?.email || userId;
      logAudit('assignment_change', 'ticket', String(ticketId), null, null, {
        added: [{ user_id: subsystemUserId, user_name: resolvedName }],
        removed: [],
      }, ticketInfo?.department_id, ticketInfo?.company_id);
    }

    // 通知被指派者（不通知自己）
    if (subsystemUserId !== assignedBy) {
      const projectName = ticketInfo?.projects?.name || '';
      const ticketLabel = ticketInfo?.ticket_no
        ? `${projectName} #${ticketInfo.ticket_no} ${ticketInfo.title || ''}`.trim()
        : (ticketInfo?.title || `Ticket #${ticketId}`);

      // 檢查被指派者的通知偏好
      const prefs = await isNotificationEnabled(subsystemUserId, 'ticket_assigned');

      // 站內通知
      if (prefs.in_app) {
        createNotification({
          user_id: subsystemUserId,
          type: 'ticket_assigned',
          title: '你被指派了一個 Ticket',
          message: ticketLabel,
          resource_type: 'ticket',
          resource_id: String(ticketId),
          actor_id: assignedBy,
        });
      }

      // Email 通知（fire-and-forget）
      if (prefs.email) {
        const assigneeEmail = targetUser?.email || data.profiles?.email;
        if (assigneeEmail) {
          sendEmailNotification({
            to: assigneeEmail,
            subject: `[Sonice] 你被指派了一個 Ticket — ${ticketLabel}`,
            html: buildEmailHtml({
              title: '你被指派了一個 Ticket',
              message: ticketLabel,
              ticketId,
              ticketLabel,
            }),
            type: 'ticket_assigned',
          });
        }
      }

      // Teams Webhook 通知（fire-and-forget，subscribed_events 由 webhook 自己控制）
      // targetEmail = 被指派者的 email
      const assigneeEmail = targetUser?.email || data.profiles?.email || null;
      sendTeamsNotification({
        user_id: subsystemUserId,
        event_type: 'ticket_assigned',
        title: '你被指派了一個 Ticket',
        message: ticketLabel,
        ticketId,
        targetEmail: assigneeEmail,
      });
    }

    await updateTicketActivity(ticketId, assignedBy);

    return { data, error: null };
  } catch (error) {
    console.error('Error adding assignee:', error);
    return { data: null, error };
  }
};

/**
 * 移除指派人（寫 assignment_change log）
 */
export const removeAssignee = async (ticketId, userId, targetUserName = null) => {
  try {
    // 若未提供名稱，先查出目標用戶 email
    let resolvedName = targetUserName;
    if (!resolvedName) {
      const { data: assigneeData } = await supabase
        .from('ticket_assignees')
        .select('profiles:user_id (email)')
        .eq('ticket_id', ticketId)
        .eq('user_id', userId)
        .single();
      resolvedName = assigneeData?.profiles?.email || userId;
    }

    const { error } = await supabase
      .from('ticket_assignees')
      .delete()
      .eq('ticket_id', ticketId)
      .eq('user_id', userId);

    if (error) throw error;

    // 查 ticket 的 company_id / department_id（用於 audit log 篩選）
    const { data: ticketInfo } = await supabase
      .from('tickets')
      .select('company_id, department_id')
      .eq('id', ticketId)
      .single();

    // 記錄取消指派異動（新格式 assignment_change）
    logAudit('assignment_change', 'ticket', String(ticketId), null, null, {
      added: [],
      removed: [{ user_id: userId, user_name: resolvedName }],
    }, ticketInfo?.department_id, ticketInfo?.company_id);

    const { profile } = await getCachedUserAndProfile(supabase);
    await updateTicketActivity(ticketId, profile?.id);

    return { error: null };
  } catch (error) {
    console.error('Error removing assignee:', error);
    return { error };
  }
};

/**
 * 替換 Owner（移除舊 Owner + 新增新 Owner 到 index[0]，寫一筆 assignment_change log）
 *
 * @param {number} ticketId
 * @param {string} oldUserId - 被替換的 Owner user_id
 * @param {string} newUserId - 新 Owner 的 user_id（主系統 ID）
 * @param {string} assignedBy - 操作者 user_id
 * @param {object|null} oldUser - 舊 Owner 的主系統資訊（用於 log 顯示名稱）
 * @param {object|null} newUser - 新 Owner 的主系統資訊（用於 ensureProfile + 通知）
 */
export const replaceAssignee = async (ticketId, oldUserId, newUserId, assignedBy, oldUser = null, newUser = null) => {
  try {
    // 1. 查出舊 Owner 名稱（用於 log）
    let oldName = oldUser?.name;
    if (!oldName) {
      const { data: oldData } = await supabase
        .from('ticket_assignees')
        .select('profiles:user_id (email)')
        .eq('ticket_id', ticketId)
        .eq('user_id', oldUserId)
        .single();
      oldName = oldData?.profiles?.email || oldUserId;
    }

    // 2. 移除舊 Owner
    const { error: deleteError } = await supabase
      .from('ticket_assignees')
      .delete()
      .eq('ticket_id', ticketId)
      .eq('user_id', oldUserId);

    if (deleteError) throw deleteError;

    // 3. 確保新 Owner 的 profile 存在
    const subsystemUserId = newUser ? await ensureProfile(newUser) : newUserId;

    // 4. 查出剩餘 assignees 的最早 assigned_at，讓新 Owner 排在最前
    const { data: remaining } = await supabase
      .from('ticket_assignees')
      .select('assigned_at')
      .eq('ticket_id', ticketId)
      .order('assigned_at', { ascending: true })
      .limit(1);

    const earliestDate = remaining?.[0]?.assigned_at
      ? new Date(new Date(remaining[0].assigned_at).getTime() - 1000).toISOString()
      : new Date().toISOString();

    // 5. 插入新 Owner
    const { data: insertedData, error: insertError } = await supabase
      .from('ticket_assignees')
      .insert({
        ticket_id: ticketId,
        user_id: subsystemUserId,
        assigned_by: assignedBy,
        assigned_at: earliestDate,
      })
      .select(`
        ticket_id,
        user_id,
        assigned_at,
        profiles:user_id (id, email)
      `)
      .single();

    if (insertError) throw insertError;

    // 6. 查 ticket info（用於 log + 通知）
    const { data: ticketInfo } = await supabase
      .from('tickets')
      .select('company_id, department_id, title, ticket_no, projects:project_id (name)')
      .eq('id', ticketId)
      .single();

    // 7. 寫一筆 assignment_change log（合併 removed + added）
    const newName = newUser?.name || insertedData?.profiles?.email || newUserId;
    logAudit('assignment_change', 'ticket', String(ticketId), null, null, {
      added: [{ user_id: subsystemUserId, user_name: newName }],
      removed: [{ user_id: oldUserId, user_name: oldName }],
    }, ticketInfo?.department_id, ticketInfo?.company_id);

    // 8. 通知新 Owner（不通知自己）
    if (subsystemUserId !== assignedBy) {
      const projectName = ticketInfo?.projects?.name || '';
      const ticketLabel = ticketInfo?.ticket_no
        ? `${projectName} #${ticketInfo.ticket_no} ${ticketInfo.title || ''}`.trim()
        : (ticketInfo?.title || `Ticket #${ticketId}`);

      const prefs = await isNotificationEnabled(subsystemUserId, 'ticket_assigned');

      if (prefs.in_app) {
        createNotification({
          user_id: subsystemUserId,
          type: 'ticket_assigned',
          title: '你被指派了一個 Ticket',
          message: ticketLabel,
          resource_type: 'ticket',
          resource_id: String(ticketId),
          actor_id: assignedBy,
        });
      }

      if (prefs.email) {
        const assigneeEmail = newUser?.email || insertedData?.profiles?.email;
        if (assigneeEmail) {
          sendEmailNotification({
            to: assigneeEmail,
            subject: `[Sonice] 你被指派了一個 Ticket — ${ticketLabel}`,
            html: buildEmailHtml({
              title: '你被指派了一個 Ticket',
              message: ticketLabel,
              ticketId,
              ticketLabel,
            }),
            type: 'ticket_assigned',
          });
        }
      }

      const assigneeEmail = newUser?.email || insertedData?.profiles?.email || null;
      sendTeamsNotification({
        user_id: subsystemUserId,
        event_type: 'ticket_assigned',
        title: '你被指派了一個 Ticket',
        message: ticketLabel,
        ticketId,
        targetEmail: assigneeEmail,
      });
    }

    await updateTicketActivity(ticketId, assignedBy);

    return { data: insertedData, error: null };
  } catch (error) {
    console.error('Error replacing assignee:', error);
    return { data: null, error };
  }
};

/**
 * 交換 Owner：將已指派的成員提升為 Owner（index[0]），原 Owner 變為一般成員
 * 不新增也不移除人，只交換順序
 */
export const swapOwner = async (ticketId, oldOwnerId, newOwnerId, assignedBy, oldUser = null, newUser = null) => {
  try {
    // 1. 取得最早的 assigned_at，讓新 Owner 排在最前面
    const { data: earliest } = await supabase
      .from('ticket_assignees')
      .select('assigned_at')
      .eq('ticket_id', ticketId)
      .order('assigned_at', { ascending: true })
      .limit(1);

    const newDate = earliest?.[0]?.assigned_at
      ? new Date(new Date(earliest[0].assigned_at).getTime() - 1000).toISOString()
      : new Date().toISOString();

    // 2. 更新新 Owner 的 assigned_at（使其成為 index[0]）
    const { error: updateError } = await supabase
      .from('ticket_assignees')
      .update({ assigned_at: newDate })
      .eq('ticket_id', ticketId)
      .eq('user_id', newOwnerId);

    if (updateError) throw updateError;

    // 3. 查 ticket info
    const { data: ticketInfo } = await supabase
      .from('tickets')
      .select('company_id, department_id')
      .eq('id', ticketId)
      .single();

    // 4. 寫 log
    const oldName = oldUser?.name || oldUser?.email || oldOwnerId;
    const newName = newUser?.name || newUser?.email || newOwnerId;
    logAudit('assignment_change', 'ticket', String(ticketId), null, null, {
      added: [{ user_id: newOwnerId, user_name: newName }],
      removed: [{ user_id: oldOwnerId, user_name: oldName }],
      type: 'owner_swap',
    }, ticketInfo?.department_id, ticketInfo?.company_id);

    await updateTicketActivity(ticketId, assignedBy);

    return { error: null };
  } catch (error) {
    console.error('Error swapping owner:', error);
    return { error };
  }
};

/**
 * 批次更新指派人員（一次處理多個新增/移除，寫一筆 log）
 */
export const batchUpdateAssignees = async (ticketId, addedIds, removedIds, assignedBy, addedUsers = [], removedUsers = []) => {
  try {
    // 1. 移除
    for (const userId of removedIds) {
      const { error } = await supabase
        .from('ticket_assignees')
        .delete()
        .eq('ticket_id', ticketId)
        .eq('user_id', userId);
      if (error) throw error;
    }

    // 2. 新增（確保 profile 存在）
    for (const userId of addedIds) {
      const user = addedUsers.find((u) => u.id === userId);
      const subsystemUserId = user ? await ensureProfile(user) : userId;

      const { error } = await supabase
        .from('ticket_assignees')
        .insert({
          ticket_id: ticketId,
          user_id: subsystemUserId,
          assigned_by: assignedBy,
        });
      if (error) throw error;
    }

    // 3. 查 ticket info
    const { data: ticketInfo } = await supabase
      .from('tickets')
      .select('company_id, department_id, title, ticket_no, projects:project_id (name)')
      .eq('id', ticketId)
      .single();

    // 4. 寫一筆 assignment_change log
    const added = addedIds.map((id) => {
      const u = addedUsers.find((u) => u.id === id);
      return { user_id: id, user_name: u?.name || u?.email || id };
    });
    const removed = removedIds.map((id) => {
      const u = removedUsers.find((u) => u.id === id);
      return { user_id: id, user_name: u?.name || u?.email || id };
    });

    if (added.length > 0 || removed.length > 0) {
      logAudit('assignment_change', 'ticket', String(ticketId), null, null, {
        added,
        removed,
      }, ticketInfo?.department_id, ticketInfo?.company_id);
    }

    // 5. 通知新增的被指派者
    for (const userId of addedIds) {
      if (userId === assignedBy) continue;
      const user = addedUsers.find((u) => u.id === userId);
      const subsystemUserId = user ? await ensureProfile(user) : userId;

      const projectName = ticketInfo?.projects?.name || '';
      const ticketLabel = ticketInfo?.ticket_no
        ? `${projectName} #${ticketInfo.ticket_no} ${ticketInfo.title || ''}`.trim()
        : (ticketInfo?.title || `Ticket #${ticketId}`);

      const prefs = await isNotificationEnabled(subsystemUserId, 'ticket_assigned');

      if (prefs.in_app) {
        createNotification({
          user_id: subsystemUserId,
          type: 'ticket_assigned',
          title: '你被指派了一個 Ticket',
          message: ticketLabel,
          resource_type: 'ticket',
          resource_id: String(ticketId),
          actor_id: assignedBy,
        });
      }

      if (prefs.email) {
        const email = user?.email;
        if (email) {
          sendEmailNotification({
            to: email,
            subject: `[Sonice] 你被指派了一個 Ticket — ${ticketLabel}`,
            html: buildEmailHtml({
              title: '你被指派了一個 Ticket',
              message: ticketLabel,
              ticketId,
              ticketLabel,
            }),
            type: 'ticket_assigned',
          });
        }
      }

      sendTeamsNotification({
        user_id: subsystemUserId,
        event_type: 'ticket_assigned',
        title: '你被指派了一個 Ticket',
        message: ticketLabel || `Ticket #${ticketId}`,
        ticketId,
        targetEmail: user?.email || null,
      });
    }

    await updateTicketActivity(ticketId, assignedBy);

    return { error: null };
  } catch (error) {
    console.error('Error batch updating assignees:', error);
    return { error };
  }
};

/**
 * 批次設定指派人（覆蓋現有指派）
 */
export const setAssignees = async (ticketId, userIds, assignedBy) => {
  try {
    // Remove all existing assignees
    const { error: deleteError } = await supabase
      .from('ticket_assignees')
      .delete()
      .eq('ticket_id', ticketId);

    if (deleteError) throw deleteError;

    // Insert new assignees
    if (userIds.length > 0) {
      const assignees = userIds.map((uid) => ({
        ticket_id: ticketId,
        user_id: uid,
        assigned_by: assignedBy,
      }));

      const { error: insertError } = await supabase
        .from('ticket_assignees')
        .insert(assignees);

      if (insertError) throw insertError;
    }

    await updateTicketActivity(ticketId, assignedBy);

    return { error: null };
  } catch (error) {
    console.error('Error setting assignees:', error);
    return { error };
  }
};
